import { makeAutoObservable, runInAction } from 'mobx';
import { adminCommerceApi } from '@/services/adminCommerceApi';
import type {
  AdminCommerceAdjustCreditsRequest,
  AdminCommerceCostChain,
  AdminCommerceCostDimensionsReply,
  AdminCommerceCostFilters,
  AdminCommerceCostRequest,
  AdminCommerceCostSeriesPoint,
  AdminCommerceCostSummaryReply,
  AdminCommerceCreditHistoryItem,
  AdminCommerceCreditOverviewReply,
} from '@/types';
import {
  buildAdminCommerceCostMultiSeries,
  buildAdminCommerceCostTrendSeries,
} from './adminCommerceCost';
import { brandStore } from './brandStore';

export interface AdminCommerceCreditHistoryViewItem
  extends AdminCommerceCreditHistoryItem {
  id: string;
}

export interface AdminCommerceCreditAssetViewItem {
  id: string;
  assetsType: string;
  remainingCredits: string;
  initialCredits: string;
  effectiveAt: string;
  expireAt: string;
  metadata: Record<string, string>;
}

export interface AdminCommerceCostSeriesViewItem
  extends AdminCommerceCostSeriesPoint {
  id: string;
}

export interface AdminCommerceCostChainViewItem extends AdminCommerceCostChain {
  id: string;
}

class AdminCommerceStore {
  searchedUserId = '';
  creditOverview: AdminCommerceCreditOverviewReply | null = null;
  creditAssets: AdminCommerceCreditAssetViewItem[] = [];
  creditHistories: AdminCommerceCreditHistoryViewItem[] = [];
  creditHistoryPagination = { page: 1, pageSize: 20, total: 0 };
  creditHistoryNextCursor = '';
  creditHistoryFilters = {
    changeType: '',
    source: '',
    status: '',
    startTime: '',
    endTime: '',
  };
  creditsLoading = false;
  historiesLoading = false;
  adjusting = false;

  costSummary: AdminCommerceCostSummaryReply | null = null;
  costSeries: AdminCommerceCostSeriesViewItem[] = [];
  costChains: AdminCommerceCostChainViewItem[] = [];
  costDimensions: AdminCommerceCostDimensionsReply = {
    providers: [],
    models: [],
    authSources: [],
    authTransports: [],
    callers: [],
    routes: [],
  };
  costFilters: AdminCommerceCostFilters = {
    interval: 'day',
    groupBy: 'providerModel',
    models: [],
    providers: [],
    authSources: [],
    callerIds: [],
  };
  costLoading = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get costTrendSeries() {
    return buildAdminCommerceCostTrendSeries(this.costSeries);
  }

  get costTrendChart() {
    return buildAdminCommerceCostMultiSeries(this.costSeries);
  }

  setSearchedUserId(userId: string) {
    this.searchedUserId = userId;
  }

  setCreditHistoryPagination(page: number, pageSize?: number) {
    this.creditHistoryPagination.page = page;
    if (pageSize) {
      this.creditHistoryPagination.pageSize = pageSize;
    }
  }

  setCreditHistoryFilters(filters: Partial<typeof this.creditHistoryFilters>) {
    this.creditHistoryFilters = {
      ...this.creditHistoryFilters,
      ...filters,
    };
    this.creditHistoryPagination.page = 1;
    this.creditHistoryNextCursor = '';
  }

  setCostFilters(filters: Partial<AdminCommerceCostFilters>) {
    this.costFilters = {
      ...this.costFilters,
      ...filters,
    };
  }

  clearCredits() {
    this.searchedUserId = '';
    this.creditOverview = null;
    this.creditAssets = [];
    this.creditHistories = [];
    this.creditHistoryPagination = { page: 1, pageSize: 20, total: 0 };
    this.creditHistoryNextCursor = '';
  }

  clearCostDashboard() {
    this.costSummary = null;
    this.costSeries = [];
    this.costChains = [];
  }

  async loadCredits(userId = this.searchedUserId) {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      return;
    }
    const appId = brandStore.requireActiveBrandAppId();
    this.searchedUserId = normalizedUserId;
    this.creditsLoading = true;
    this.historiesLoading = true;
    try {
      const [overview, histories] = await Promise.all([
        adminCommerceApi.getCreditsOverview({
          userId: normalizedUserId,
          appId,
        }),
        adminCommerceApi.listCreditHistories({
          userId: normalizedUserId,
          appId,
          grouped: true,
          ...this.creditHistoryFilters,
          page: 1,
          pageSize: this.creditHistoryPagination.pageSize,
        }),
      ]);
      runInAction(() => {
        const normalizedOverview = {
          ...overview,
          creditsByType: overview.creditsByType ?? [],
          creditAssets: overview.creditAssets ?? [],
        };
        this.creditOverview = normalizedOverview;
        this.creditAssets = normalizedOverview.creditAssets.map(
          (item, index) => ({
            id: `${item.assetsType}-${item.effectiveAt}-${item.expireAt}-${index}`,
            ...item,
          }),
        );
        this.creditHistories = (histories.items ?? []).map((item) => ({
          id: item.historyNo,
          ...item,
        }));
        this.creditHistoryPagination.total = histories.total;
        this.creditHistoryNextCursor = histories.nextCursor || '';
        this.creditsLoading = false;
        this.historiesLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.creditsLoading = false;
        this.historiesLoading = false;
      });
      throw error;
    }
  }

  async loadCreditHistories() {
    if (!this.searchedUserId) {
      return;
    }
    const appId = brandStore.requireActiveBrandAppId();
    this.historiesLoading = true;
    try {
      const response = await adminCommerceApi.listCreditHistories({
        userId: this.searchedUserId,
        appId,
        grouped: true,
        ...this.creditHistoryFilters,
        page: 1,
        pageSize: this.creditHistoryPagination.pageSize,
      });
      runInAction(() => {
        this.creditHistories = (response.items ?? []).map((item) => ({
          id: item.historyNo,
          ...item,
        }));
        this.creditHistoryPagination.total = response.total;
        this.creditHistoryNextCursor = response.nextCursor || '';
        this.historiesLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.historiesLoading = false;
      });
      throw error;
    }
  }

  async loadMoreCreditHistories() {
    const cursor = this.creditHistoryNextCursor;
    if (!this.searchedUserId || !cursor || this.historiesLoading) {
      return;
    }
    const appId = brandStore.requireActiveBrandAppId();
    this.historiesLoading = true;
    try {
      const response = await adminCommerceApi.listCreditHistories({
        userId: this.searchedUserId,
        appId,
        grouped: true,
        cursor,
        ...this.creditHistoryFilters,
        page: 1,
        pageSize: this.creditHistoryPagination.pageSize,
      });
      runInAction(() => {
        const existingIDs = new Set(
          this.creditHistories.map((item) => item.id),
        );
        const nextItems = (response.items ?? [])
          .map((item) => ({
            id: item.historyNo,
            ...item,
          }))
          .filter((item) => !existingIDs.has(item.id));
        this.creditHistories = [...this.creditHistories, ...nextItems];
        this.creditHistoryNextCursor = response.nextCursor || '';
        this.historiesLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.historiesLoading = false;
      });
      throw error;
    }
  }

  async adjustCredits(input: Omit<AdminCommerceAdjustCreditsRequest, 'appId'>) {
    const appId = brandStore.requireActiveBrandAppId();
    this.adjusting = true;
    try {
      const response = await adminCommerceApi.adjustCredits({
        ...input,
        appId,
      });
      await this.loadCredits(input.userId);
      runInAction(() => {
        this.adjusting = false;
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.adjusting = false;
      });
      throw error;
    }
  }

  async loadCostDashboard() {
    const appId = brandStore.requireActiveBrandAppId();
    const request: AdminCommerceCostRequest = {
      ...this.costFilters,
      appId,
    };
    this.costLoading = true;
    try {
      const [summary, series, dimensions, chains] = await Promise.all([
        adminCommerceApi.getCostSummary(request),
        adminCommerceApi.listCostSeries(request),
        adminCommerceApi.getCostDimensions(request),
        adminCommerceApi.listCostChains(request),
      ]);
      runInAction(() => {
        this.costSummary = summary;
        this.costSeries = (series.items ?? []).map((item) => ({
          id: `${item.bucketAt}-${item.groupKey || 'all'}-${item.authSource || 'unknown'}-${item.callerId || 'all'}`,
          ...item,
        }));
        this.costDimensions = {
          providers: dimensions.providers ?? [],
          models: dimensions.models ?? [],
          authSources: dimensions.authSources ?? [],
          authTransports: dimensions.authTransports ?? [],
          callers: dimensions.callers ?? [],
          routes: dimensions.routes ?? [],
        };
        this.costChains = (chains.items ?? []).map((item, index) => ({
          id: `${item.authSource}-${item.callerId}-${item.provider}-${item.routeId}-${item.publicModel}-${item.upstreamModel}-${index}`,
          ...item,
        }));
        this.costLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.costLoading = false;
      });
      throw error;
    }
  }
}

export const adminCommerceStore = new AdminCommerceStore();
